import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { DifficultyBadge } from "@/components/cards";
import { api } from "@/lib/api";
import { addRecent, toggleFavorite, isFavorite } from "@/lib/local";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Clock, Star, Share2, Link2 } from "lucide-react";
import { TutorialFeedback } from "@/components/TutorialFeedback";
import { PdfButton } from "@/components/PdfButton";
import { Seo } from "@/components/Seo";
import { API, track } from "@/lib/api";

export default function TutorialDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [tut, setTut] = useState(null);
  const [all, setAll] = useState([]);
  const [fav, setFav] = useState(false);

  useEffect(() => {
    api.get(`/tutorials/${slug}`).then((r) => {
      setTut(r.data);
      addRecent(r.data);
      setFav(isFavorite(slug));
    }).catch(() => nav("/"));
    api.get("/tutorials").then((r) => setAll(r.data));
    api.post("/analytics/track", { type: "tutorial_view", label: slug }).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug, nav]);

  if (!tut) return <Layout><div className="max-w-3xl mx-auto px-4 py-20 text-gray-400">Carregando...</div></Layout>;

  const idx = all.findIndex((t) => t.slug === slug);
  const prev = idx > 0 ? all[idx - 1] : null;
  const next = idx >= 0 && idx < all.length - 1 ? all[idx + 1] : null;

  const onFav = () => {
    toggleFavorite(tut);
    const n = isFavorite(slug);
    setFav(n);
    toast.success(n ? "Adicionado aos favoritos ⭐" : "Removido dos favoritos");
  };

  const shareUrl = `${API}/share/tutorial/${slug}`;
  const shareText = `Tutorial Club Infinity: ${tut.title}`;
  const firstImage = tut.steps.find((s) => s.image)?.image;
  const shareWhatsApp = () => {
    track("share", slug);
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`, "_blank", "noopener");
  };
  const copyLink = async () => {
    track("share", `${slug}:copy`);
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copiado!"); } catch { toast.error("Não foi possível copiar."); }
  };
  const jsonLd = {
    "@context": "https://schema.org", "@type": "HowTo", name: tut.title,
    description: `Como ${tut.title.toLowerCase()} — ${tut.time_estimate}, dificuldade ${tut.difficulty}.`,
    totalTime: undefined, image: firstImage,
    step: tut.steps.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.description, image: s.image || undefined })),
  };

  return (
    <Layout>
      <Seo title={tut.title} path={`/tutorial/${slug}`} type="article" image={firstImage}
        description={`Tutorial passo a passo (${tut.steps.length} passos · ${tut.time_estimate} · ${tut.difficulty}). Configure seu ${tut.device || "dispositivo"} com o Club Infinity.`}
        jsonLd={jsonLd} />
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-14">
        <Link to="/dispositivos" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-cyan-300 mb-6" data-testid="tutorial-back">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>

        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            {tut.category && <div className="text-xs font-semibold uppercase tracking-widest text-amber-400 mb-2">{tut.category}</div>}
            <h1 className="text-2xl sm:text-3xl font-bold">{tut.title}</h1>
          </div>
          <button onClick={onFav} data-testid="tutorial-favorite-btn"
            className={`shrink-0 p-2.5 rounded-full border transition ${fav ? "bg-amber-400/20 border-amber-400/50 text-amber-300" : "bg-white/5 border-white/10 text-gray-400 hover:text-amber-300"}`}>
            <Star className="w-5 h-5" fill={fav ? "currentColor" : "none"} />
          </button>
        </div>

        <div className="flex items-center gap-3 flex-wrap mb-8">
          {tut.app && <span className="text-sm text-gray-400">App: <span className="text-gray-200">{tut.app}</span></span>}
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-400"><Clock className="w-4 h-4" /> {tut.time_estimate}</span>
          <DifficultyBadge level={tut.difficulty} />
          <span className="flex-1" />
          <PdfButton tutorial={tut} compact />
          <button onClick={shareWhatsApp} data-testid="tutorial-share-whatsapp" title="Compartilhar no WhatsApp"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20 transition">
            <Share2 className="w-3.5 h-3.5" /> Compartilhar
          </button>
          <button onClick={copyLink} data-testid="tutorial-copy-link" title="Copiar link"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10 transition">
            <Link2 className="w-3.5 h-3.5" /> Copiar link
          </button>
        </div>

        <div className="space-y-4">
          {tut.steps.map((s, i) => (
            <div key={s.key || `${i}-${s.title}`} data-testid={`tutorial-step-${i + 1}`} className="ci-card p-5 flex gap-4">
              <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center font-bold text-sm">{i + 1}</div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">{s.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{s.description}</p>
                {s.image && <img src={s.image} alt={s.title} className="mt-3 rounded-lg border border-white/10 w-full" />}
              </div>
            </div>
          ))}
        </div>

        {/* nav */}
        <div className="flex justify-between gap-3 mt-8">
          {prev ? (
            <button onClick={() => nav(`/tutorial/${prev.slug}`)} data-testid="tutorial-prev-btn" className="inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-cyan-300">
              <ArrowLeft className="w-4 h-4" /> Anterior
            </button>
          ) : <span />}
          {next && (
            <button onClick={() => nav(`/tutorial/${next.slug}`)} data-testid="tutorial-next-btn" className="inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-cyan-300">
              Próximo <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        <TutorialFeedback key={slug} slug={slug} />
        <div className="ci-card p-6 mt-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="tutorial-pdf-card">
          <div className="flex-1">
            <h3 className="font-bold">Prefere guardar este tutorial?</h3>
            <p className="text-sm text-gray-400">Abra o PDF em uma nova aba ou salve no seu celular ou computador.{!tut.pdf_url && " Gerado automaticamente com os passos e fotos deste tutorial."}</p>
          </div>
          <PdfButton tutorial={tut} />
        </div>
      </div>
    </Layout>
  );
}
